import { map as asyncMap, series as asyncSeries } from "async";
import merge from "deepmerge";
import { BaseService } from "../baseService";
import * as storeUtils from "./storeUtils";
import StorageClient from "../../clients/storageClient";
import ConfigClient from "../../clients/configClient";
import Logger from "../../clients/logger";
import SystemManagerClient from "../../common/systemManagerClient";
import { StandardCallback, StandardError } from "../../types";

Logger.start();

export type DistributedStore = {
	name: string;
	persist?: boolean;
	global?: boolean;
	values: Record<string, any>;
};

type DistributedStoreMappings = Record<string, any>;

/**
 * Ensures that a store object has a "name" member (not a "store" member).
 *
 * DistributedStoreClient and some legacy config/storage used `.store`.
 */
const scrubStore = (original: DistributedStore) => {
	let scrubbed: DistributedStore = {
		...original,
		name: original.name ?? (original as any).store,
	};
	delete (scrubbed as any).store;
	return scrubbed;
};

// /We use global if no store is set
class StoreService extends BaseService {
	persistingStores = {};

	stores: Record<string, DistributedStore> = {};

	// Each store will be index here with an flattened object attached that hold the location of each object and it's parent.
	mappings: DistributedStoreMappings = {};

	constructor() {
		super({
			name: "dataStoreService",
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});
	}

	// Send out the actual notification
	publishFieldUpdate(store: DistributedStore, field: string, topic: string, value: any) {
		// Sends the message for each field
		this.RouterClient.publish(`storeService${store.name}.${field}`, {
			topic,
			field,
			value,
			store: store.name,
		});
	}

	/**
	 * Publishes our updates to an object. This will crawl the tree backwards and send out
	 * notifications. `parent.child`-- If you changed the child value, anything listening
	 * on the parent channel will also ge the message.
	 * @param {storeObject} store Store to crawl
	 * @param {string} startField Field to start on.
	 * @param {string} topic topic to publish on
	 * @param {*} mappings
	 */
	publishObjectUpdates(store: DistributedStore, startField: string, topic: string, mappings: DistributedStoreMappings) {
		this.RouterClient.publish(`storeService${store.name}`, {
			topic: "storeChanged",
			store: store.name,
			storeData: store,
		});
		const currentMapping = mappings;

		// Loop through the mapping for each item's parent.
		while (startField) {
			this.publishFieldUpdate(store, startField, topic, storeUtils.byString(store.values, startField));
			startField = currentMapping[startField];
		}
	}

	// Send out our notification to anything listening on these channels. If you remove an object we need to make sure anything that was listening on that channel gets informed.
	publishRemovals(store: DistributedStore, removals: string[]) {
		for (let i = 0; i < removals.length; i++) {
			this.RouterClient.publish(`storeService${store.name}.${removals[i]}`, {
				topic: "removal",
				field: removals[i],
				value: storeUtils.byString(store.values, removals[i]),
				store: store.name,
			});
		}
	}

	/**
	 * Sets a field/value in the specified global store
	 */
	setValue(params: { store: string; field: string; value: any }) {
		if (!params.field) {
			return Logger.system.error("no field provided", params);
		}
		if (!params.hasOwnProperty("value")) {
			return Logger.system.error("no value provided", params);
		}

		let topic = "valueChanged";

		if (!this.stores[params.store]) {
			this.stores[params.store] = {
				values: {},
				name: params.store,
			};
		}

		const storeObject = this.stores[params.store];

		if (!storeObject.values[params.field]) {
			topic = "valueAdded";
		}
		const removals = storeUtils.checkForObjectChange(storeObject.values, params.field, this.mappings[params.store]);
		// Update the values in the store
		storeUtils.setPath(storeObject.values, params.field, params.value);

		const mapping = this.mappings[params.store];

		storeUtils.mapField(storeObject.values, params.field, mapping); // remap
		if (removals) {
			this.publishRemovals(storeObject, removals);
		}
		if (storeObject.persist)
			StorageClient.save({
				topic: "finsemble",
				key: `distributedStore-${storeObject.name}`,
				value: storeObject,
			});
		this.publishObjectUpdates(storeObject, params.field, topic, this.mappings[params.store]);
	}

	/**
	 * Creates a global store. The store is persisted (saved to StorageClient) if the persist flag is true.
	 */
	createStore(storeDefinition: DistributedStore, cb?: (error: null | string, store?: DistributedStore) => void) {
		const { name } = storeDefinition;
		if (!name) {
			const error = `DistributedStoreService.createStore(). No store name provided: ${storeDefinition}`;
			console.error(error);
			if (cb) cb(error);
			return;
		}
		if (!this.stores[name]) {
			this.mappings[name] = {};

			this.stores[name] = {
				name,
				persist: storeDefinition.persist,
				global: true,
				values: storeDefinition.values ? storeDefinition.values : {},
			};
			storeUtils.initObject(this.stores[name].values, null, this.mappings[name]);
			if (storeDefinition.persist) {
				// save the store into storage
				this.persistingStores[name] = name;
				// This is just a reference to persisting stores;
				StorageClient.save({
					topic: "finsemble",
					key: "distributedStores",
					value: this.persistingStores,
				});
				// save the store. Having a separate record here makes saves much smaller.
				StorageClient.save({
					topic: "finsemble",
					key: `distributedStore-${name}`,
					value: this.stores[name],
				});
			}
		}
		this.RouterClient.publish(`storeService${storeDefinition}`, {
			topic: "storeCreated",
			store: this.stores[name],
		});
		if (cb) cb(null, this.stores[name]);
	}

	// Retrieves a store and passes it to the requestor.
	getStore(params: { store: string }, cb: (error: string | null, value?: DistributedStore) => void) {
		if (!params.store) {
			return cb("no store provided");
		}
		if (!this.stores[params.store]) {
			return cb(`store not found: ${params.store}`);
		}
		const store = this.stores[params.store];

		/**
		 * September 2021
		 * Previous versions of distributedStoreClient expected `store.store` (instead of store.name)
		 * to contain the name of the store. We should keep this in place until customers are safely migrated.
		 */
		const backwardCompatibleStore = {
			...store,
			store: store?.name,
		};
		return cb(null, backwardCompatibleStore);
	}

	// remove a store
	removeStore(params: { store: string }, cb: (error: string | null, value?: boolean) => void) {
		if (params.store && this.stores[params.store]) {
			if (this.stores[params.store].persist) {
				delete this.persistingStores[params.store]; // delete the store from our local list
				// remove the store from our list
				StorageClient.save({
					topic: "finsemble",
					key: "distributedStores",
					value: this.persistingStores,
				});
				// remove the store
				StorageClient.remove({
					topic: "finsemble",
					key: `distributedStore-${params.store}`,
				});
			}

			delete this.stores[params.store];
			this.RouterClient.publish(`storeService${params.store}`, {
				topic: "storeDeleted",
				store: params.store,
			});
			return cb(null, true);
		}
		return cb(null, false);
	}

	// Get a value out of the store
	getValue(params: { store: string; field: string }, cb: (error: string | null, value?: any) => void) {
		let store = params.store || "finsemble";
		if (!params.field) {
			return cb("no field provided");
		}
		if (!this.stores[store]) {
			return cb(`store:${store} does not exist.${JSON.stringify(this.stores)}`);
		}
		const fieldValue = storeUtils.byString(this.stores[store].values, params.field);
		return cb(null, fieldValue);
	}

	// Get multiple values from a store
	getValues(params: { store: string; fields: { field: string }[] }, cb: StandardCallback<string | null, any>) {
		let store = params.store || "finsemble";
		const storeObject = this.stores[store];
		if (!storeObject) {
			return cb(`store:${store} does not exist.${JSON.stringify}`);
		}

		// If fields isn't specified then respond with the entire store
		if (!params.fields) {
			return cb(null, storeObject.values);
		}

		const values = {};
		for (let i = 0; i < params.fields.length; i++) {
			const item = params.fields[i];
			const field = typeof item === "string" ? item : item.field;
			const fieldValue = storeUtils.byString(storeObject.values, field);
			values[field] = fieldValue;
		}
		return cb(null, values);
	}

	/**
	 *
	 * @param {*} params
	 * @param {String} params.field - allows you to do a merge at a lower level in the values object
	 * @param {Object} params.values - the values to merge into the store values
	 * @param {*} cb
	 */
	mergeValues(
		params: { store: string; field?: string; values: Record<string, any>; preserveState?: boolean },
		cb: () => void
	) {
		// This only works on load (no deep merge).
		if (!params.hasOwnProperty("values")) {
			Logger.system.error("no values provided", params);
			return cb();
		}

		if (!this.stores[params.store]) {
			this.stores[params.store] = {
				values: {},
				name: params.store,
			};
		}

		const storeObject = this.stores[params.store];

		const overwriteMerge = (destinationArray, sourceArray) => sourceArray;

		// If preserveState configure the merge to overwrite lists from the foundation with lists that already exist at the same location
		// in Finsemble's memory
		if (params.preserveState) {
			Logger.system.verbose("Merging distributed stores; preserving previous state");

			// Deepmerge will overwrite all lists with the contents of the sourceArray. When preserving state, the source array
			// will be the data already contained in Finsemble's distributed store
			if (params.field) {
				storeObject.values[params.field] = merge(params.values, storeObject.values[params.field], {
					arrayMerge: overwriteMerge,
				});
			} else {
				storeObject.values = merge(params.values, storeObject.values, {
					arrayMerge: overwriteMerge,
				});
			}
		} else {
			// When not preserving state, the source array will come from the foundation and overwrite any conflicting state
			// contained in Finsemble
			if (params.field) {
				storeObject.values[params.field] = merge(storeObject.values[params.field], params.values, {
					arrayMerge: overwriteMerge,
				});
			} else {
				storeObject.values = merge(storeObject.values, params.values, {
					arrayMerge: overwriteMerge,
				});
			}

			// If the foundation should not respect previous state,
			// remove prop keeping track of deleted keys
			delete storeObject.values.deleted;
		}

		if (storeObject.persist) {
			StorageClient.save({
				topic: "finsemble",
				key: `distributedStore-${storeObject.name}`,
				value: storeObject,
			});
		}

		// publish out a store change
		this.RouterClient.publish(`storeService${storeObject.name}`, {
			topic: "storeChanged",
			store: storeObject.name,
			storeData: storeObject,
		});
		if (cb) cb();
	}

	/**
	 * Initializes the service
	 */
	load(cb) {
		this.RouterClient.addPubSubResponder(/storeService*/, {});
		this.RouterClient.addPubSubResponder("distributedStoresLoading", {
			State: "start",
		});

		this.RouterClient.addResponder("storeService.getValue", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.getValue(message.data, (getValueError, data) => {
				message.sendQueryResponse(getValueError, data);
			});
		});

		this.RouterClient.addResponder("storeService.getValues", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.getValues(message.data, (getValuesError, data) => {
				message.sendQueryResponse(getValuesError, data);
			});
		});

		this.RouterClient.addResponder("storeService.createStore", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}
			return this.createStore(scrubStore(message.data as DistributedStore), (createStoreError, data) => {
				/**
				 * September 2021
				 * Previous versions of distributedStoreClient expected `store.store` (instead of store.name)
				 * to contain the name of the store. We should keep this in place until customers are safely migrated.
				 */
				const backwardCompatibleStore = {
					...data,
					store: data?.name,
				};

				message.sendQueryResponse(createStoreError, backwardCompatibleStore);
			});
		});

		this.RouterClient.addResponder("storeService.setValue", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}

			this.setValue(message.data);
			message.sendQueryResponse(err, message.data);
		});

		this.RouterClient.addResponder("storeService.getStore", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}

			this.getStore(message.data, (getStoreError, data) => {
				message.sendQueryResponse(getStoreError, data);
			});
		});

		this.RouterClient.addResponder("storeService.removeStore", (err, message) => {
			if (err || !message) {
				return Logger.system.error(err);
			}

			this.removeStore(message.data, (removeStoreError, data) => {
				message.sendQueryResponse(removeStoreError, data);
			});
		});

		SystemManagerClient.waitForBootStage("authentication", "stageCompleted", () => {
			this.loadPersistingStores();
		});

		cb();
	}

	/**
	 * This loads stores from storage on start. It then checks configs for any new/edits to stores.
	 */
	loadPersistingStores() {
		const tasks = [
			(done) => {
				// we load all of our stores first

				StorageClient.get(
					{ topic: "finsemble", key: "distributedStores" },
					(err, loadedStores?: Record<string, DistributedStore>) => {
						if (err) return done(err);
						if (!loadedStores) return done();

						const storeKeys = Object.keys(loadedStores);
						asyncMap(
							storeKeys,
							(store: string, storeDone: (error?: StandardError) => void) => {
								StorageClient.get(
									{ topic: "finsemble", key: `distributedStore-${store}` },
									(getError, retrievedStore?: DistributedStore) => {
										if (getError) return storeDone(getError);
										if (!retrievedStore) {
											Logger.system.error("DISTRIBUTEDSTORESERVICE", "no store found:", store);
											return storeDone("no store found");
										}
										this.createStore(scrubStore(retrievedStore), storeDone);
									}
								);
							},
							(asyncError) => {
								done(asyncError);
							}
						);
					}
				);
			},
			(done) => {
				type DistributedStoreConfigEntry = {
					name: string;
					default: Record<string, any>;
					foundation: Record<string, any>;
				};
				type DistributedStoreConfig = {
					initialStores?: DistributedStoreConfigEntry[];
				};
				// load stores from the config
				ConfigClient.getValue<DistributedStoreConfig>(
					{ field: "finsemble.servicesConfig.distributedStore" },
					(err, config) => {
						if (err) return done(err);
						if (!config) {
							// the config isn't setup
							Logger.system.log("DISTRIBUTEDSTORESERVICE", "servicesConfig.distributedStore does not exist");
							return done();
						}
						Logger.system.log("DISTRIBUTEDSTORESERVICE", "serviceConfig", config);
						// no initial stores
						if (!config.initialStores || !config.initialStores.length) {
							Logger.system.log("DISTRIBUTEDSTORESERVICE", "No initialStores found");
							return done();
						}
						asyncMap(
							config.initialStores,
							(store: DistributedStoreConfigEntry, storeDone: () => void) => {
								let storeExists = false;
								if (this.stores[store.name]) {
									storeExists = true;
								}
								Logger.system.log("DISTRIBUTEDSTORESERVICE", "creating a new store", store);
								this.createStore(
									{
										persist: true,
										global: true,
										name: store.name ?? (store as any).store,
										values: store.default,
									},
									() => {
										if (!store.foundation || storeExists) return storeDone();
										return this.mergeValues(
											{
												store: store.name,
												values: store.foundation,
											},
											storeDone
										);
									}
								);
							},
							(asyncError) => {
								done(asyncError);
							}
						);
					}
				);
			},
		];

		asyncSeries(tasks, (err: StandardError) => {
			if (err) {
				return Logger.system.error("Data Store persist store load Error:", err);
			}
			// This message gets published *after* the service starts up because persistant stores
			// are not loaded until after the authentication stage. This nuance is handled inside of
			//  distributedStoreClient.ts
			this.RouterClient.publish("distributedStoresLoading", { State: "done" });
			Logger.system.log("Data store loaded from all persistent stores");
		});
	}
}

const serviceInstance = new StoreService();
serviceInstance.onBaseServiceReady((callback) => {
	serviceInstance.load(callback);
});

serviceInstance.start();
export default serviceInstance;
